import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import AppCtaLink from '../AppCtaLink';
import { Reveal, staggerParent, staggerItem } from '../motion/Reveal';

const BUBBLES = [
  { src: '/assets/lumi/world/bez_ALL_07_pocket_friend.webp', className: 'lumi-hero__bubble--a' },
  { src: '/assets/lumi/world/poshuk_ALL_06_untangle_thoughts.webp', className: 'lumi-hero__bubble--b' },
  { src: '/assets/lumi/world/robota_ALL_07_after_shift_voice.webp', className: 'lumi-hero__bubble--c' }
];

/** Visual rewrite of #hero. Parent section keeps aurora, spot and scroll cue. */
const LumiHero = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  return (
    <div className="container">
      <div className="hero-content lumi-hero">
        <motion.div className="hero-text" {...staggerParent(reduceMotion)}>
          <motion.p className="lumi-hero__brand" {...staggerItem(reduceMotion)}>
            {t('lumi.hero.brand_line')}
          </motion.p>
          <motion.h1 className="hero-title" {...staggerItem(reduceMotion)}>
            {t('lumi.hero.title')}
          </motion.h1>
          <motion.p className="hero-subtitle" {...staggerItem(reduceMotion)}>
            {t('lumi.hero.subtitle')}
          </motion.p>

          <motion.div className="lumi-hero__actions" {...staggerItem(reduceMotion)}>
            <AppCtaLink platform="web" placement="home_hero" className="lumi-hero__web">
              <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
                <path fill="currentColor" d="M12 2a10 10 0 1 0 .01 20.01A10 10 0 0 0 12 2zm6.9 9h-3.18a15.7 15.7 0 0 0-1.2-5.02A8.03 8.03 0 0 1 18.9 11zM12 4c.9 1.16 1.7 3.05 2.05 5H9.95C10.3 7.05 11.1 5.16 12 4zM4.26 13h3.36a17.2 17.2 0 0 0 0-2H4.26a8.06 8.06 0 0 0 0 2zm.84 2h3.18a15.7 15.7 0 0 0 1.2 5.02A8.03 8.03 0 0 1 5.1 15zm3.18-6H5.1a8.03 8.03 0 0 1 4.38-5.02A15.7 15.7 0 0 0 8.28 9zM12 20c-.9-1.16-1.7-3.05-2.05-5h4.1c-.35 1.95-1.15 3.84-2.05 5zm2.34-7H9.66a15.4 15.4 0 0 1 0-2h4.68a15.4 15.4 0 0 1 0 2zm.18 6.02A15.7 15.7 0 0 0 15.72 15h3.18a8.03 8.03 0 0 1-4.38 5.02zM19.74 13a8.06 8.06 0 0 0 0-2h-3.36a17.2 17.2 0 0 0 0 2h3.36z" />
              </svg>
              <span>
                <small>Web</small>
                {t('home.cta')}
              </span>
            </AppCtaLink>
            <div className="lumi-hero__stores">
              <AppCtaLink
                platform="android"
                placement="home_hero"
                className="lumi-hero__store"
                aria-label={t('home.value_section.apps.google')}
              >
                <img src="/images/google_en.svg" alt="" width="135" height="40" decoding="async" />
              </AppCtaLink>
              <AppCtaLink
                platform="ios"
                placement="home_hero"
                className="lumi-hero__store"
                aria-label={t('home.value_section.apps.apple')}
              >
                <img src="/images/apple_en.svg" alt="" width="135" height="40" decoding="async" />
              </AppCtaLink>
            </div>
          </motion.div>

          <motion.dl className="lumi-hero__facts" {...staggerItem(reduceMotion)}>
            <div>
              <dt>24/7</dt>
              <dd>{t('home.stats.availability')}</dd>
            </div>
            <div>
              <dt>100%</dt>
              <dd>{t('home.stats.confidentiality')}</dd>
            </div>
            <div className="lumi-hero__fact-line">
              {/* CLM-001 expires 2026-11-16: re-review before then */}
              <dt>70K+</dt>
              <dd>{t('home.stats.free_subscriptions', t('home.stats.active_users'))}</dd>
            </div>
          </motion.dl>
        </motion.div>

        <Reveal className={`hero-image lumi-hero__stage${reduceMotion ? ' is-still' : ''}`} y={24} delay={0.12}>
          <img
            className={`lumi-hero__mascot${reduceMotion ? ' is-still' : ''}`}
            src="/assets/lumi/brand/lumi-mascot.webp"
            alt={t('lumi.hero.mascot_alt')}
            width="800"
            height="800"
            fetchPriority="high"
            decoding="async"
          />
          <div className="lumi-hero__bubbles" aria-hidden="true">
            {BUBBLES.map((bubble) => (
              <img
                key={bubble.src}
                className={`lumi-hero__bubble ${bubble.className}`}
                src={bubble.src}
                alt=""
                width="160"
                height="160"
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default LumiHero;
